import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, EditorField } from '../@types/editor'
import type { DatePart, DateRanges, DateSelection } from '../utils/editorDate'

import { DATE_PART_BY_FIELD_NAME } from '../constants/dateLottery'
import { pickDateCompletion } from '../utils/editorDate'
import {
  isFieldComplete,
  isPlayComplete as isPlayCompleteHelper,
  setFieldSelected,
} from '../utils/editorFields'
import { editorStorage } from '../utils/editorPersistence'
import { createPlayKey, hasDeletablePlays } from '../utils/editorPlay'
import { createLotteryStoreCache, createPlaysState } from '../utils/editorStore'

export type EditorPlay = {
  fields: EditorField[]
  id: string
  quickPick?: string
  key: string
}

const EMPTY_SELECTION: DateSelection = { day: null, month: null, year: null }

const DEFAULT_RANGES: DateRanges = {
  day: { max: 31, min: 1 },
  month: { max: 12, min: 1 },
  year: { max: 99, min: 0 },
}

const toPart = (name: string): Maybe<DatePart> => DATE_PART_BY_FIELD_NAME[name] ?? null

const toRanges = (config: Config): DateRanges => {
  const ranges = { ...DEFAULT_RANGES }

  config.numbers.forEach(({ name, selectableNumbers }) => {
    const part = toPart(name)
    if (part && selectableNumbers.length) {
      ranges[part] = { max: Math.max(...selectableNumbers), min: Math.min(...selectableNumbers) }
    }
  })

  return ranges
}

const toSelection = (play: EditorPlay): DateSelection => {
  const selection = { ...EMPTY_SELECTION }

  play.fields.forEach(({ name, selected }) => {
    const part = toPart(name)
    if (part) selection[part] = selected[0] ?? null
  })

  return selection
}

const createPlay = (config: Config, index: number): EditorPlay => ({
  fields: config.numbers.map(({ name }) => ({ name, selected: [] })),
  id: `play-${index + 1}`,
  key: createPlayKey(),
})

const getIgnoredFieldNames = (): string[] => []

const isPlayComplete = (config: Config, play: EditorPlay) =>
  isPlayCompleteHelper(config, play, isFieldComplete)

const quickPickPlay = (config: Config, play: EditorPlay) => {
  const locked = isPlayComplete(config, play) ? EMPTY_SELECTION : toSelection(play)
  const picked = pickDateCompletion(toRanges(config), locked)

  play.fields.forEach((field) => {
    const part = toPart(field.name)
    if (!part) return

    const value = picked[part]
    field.selected = value === null || value === undefined ? [] : [value]
  })
}

const createStore = (lotteryId: LotteryId) =>
  defineStore(
    `ltc_date_lottery_${lotteryId.toLowerCase()}`,
    () => {
      const playsState = createPlaysState({ createPlay, isPlayComplete, lotteryId, quickPickPlay })
      const { getPlay, plays, removeEmptyPlaysExcept } = playsState

      const canDeleteAllPlays = (config: Config) =>
        hasDeletablePlays(plays.value, (play) => isPlayComplete(config, play))

      const clearPlay = (playId: string) => {
        const play = getPlay(playId)
        if (!play) return
        play.fields.forEach((field) => (field.selected = []))
        removeEmptyPlaysExcept(playId)
      }

      const setSelectedNumbers = (playId: string, fieldIndex: number, selected: number[]) =>
        setFieldSelected(getPlay(playId), fieldIndex, selected)

      return {
        ...playsState,
        canDeleteAllPlays,
        clearPlay,
        getIgnoredFieldNames,
        isPlayComplete,
        setSelectedNumbers,
      }
    },
    {
      persist: {
        omit: ['expandedPlayId', 'pendingRemovals'],
        storage: editorStorage(),
      },
    },
  )

export const useDateLotteryStore = createLotteryStoreCache(createStore)
